import NextImage from 'next/image';
import { Text } from '../../../components/Text/Text';
import { Avatar, AvatarSize } from '../../../components/Avatar/Avatar';
import { InternalLink } from '../../../components/Clickables/InternalLink/InternalLink';
import { ContinuousCarousel } from '../../../components/Carousels/ContinuousCarousel/ContinuousCarousel';
import { convertWeight } from '@lib/units/convertWeight';
import { getRoute, Source } from '../helpers/getRoute';

export interface Collector {
  id: string;
  username: string;
  stateName: string;
  weightInKg: number;
  avatarUrl?: string;
  photoUrl: string;
  description?: string;
}

const CollectorCard = ({ collector }: { collector: Collector }) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-1 px-2">
      <Avatar alt={collector.username} src={collector.avatarUrl} size={AvatarSize.DROPDOWN_SMALL} />
      <Text variant="xs" className="text-abyss truncate">
        {collector.username}
      </Text>
    </div>
    <div className="flex flex-col">
      <div className="bg-lake-fog relative aspect-square w-full overflow-hidden rounded-lg">
        <NextImage
          src={collector.photoUrl}
          alt={`${collector.username}'s trash collection`}
          fill
          className="object-cover"
          sizes="171px"
        />
      </div>
      {collector.description && (
        <Text variant="sm" className="mb-2">
          {collector.description}
        </Text>
      )}

      <div className="flex flex-col gap-0.5">
        <Text variant="sm" className="m-0">
          {collector.stateName}
        </Text>
        <Text variant="sm" className="text-trench m-0 font-semibold">
          {convertWeight(collector.weightInKg, 'lb')}
        </Text>
      </div>
    </div>
  </div>
);

interface IProps {
  collectors: Collector[];
  source?: Source;
}

export const CollectorsCarousel = ({ collectors, source = 'web' }: IProps) => (
  <section className="flex w-full flex-col gap-6">
    <ContinuousCarousel
      dataTestId="million-pound-collectors"
      title="See what others collect"
      headingVariant="md"
      headingAs="h2"
    >
      {collectors.map(collector => (
        <CollectorCard key={collector.id} collector={collector} />
      ))}
    </ContinuousCarousel>
    <div className="flex justify-center">
      <InternalLink to={getRoute(source, '/feed')} variant="buttonSecondary" size="sm">
        Go to feed
      </InternalLink>
    </div>
  </section>
);
